// src/components/HowToGuide.js
// ---------------------------------------------------------------------------
// "How do I…?" - where to add everything, step by step, with "Take me there"
// buttons that open the exact screen (and the right popup when there is one).
// Topics live in src/utils/howToTopics.js.
//
// Open from: the reminder popups on Home (Missed jobs / Reminders), Team
// Dashboard → Reminders, and Admin Portal → ⋯ More → "How do I…?".
//
//   <HowToGuide open onClose={..} initialTopic="set-sub-pay" onNavigate={closeParentPopup} />
//   <HowToButton topic="set-sub-pay" onNavigate={..} />   (button + guide)
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { HOW_TO_GROUPS, HOW_TO_TOPICS } from "../utils/howToTopics";
import { goTo } from "../utils/appLinks";
import "./HowToGuide.css";

export default function HowToGuide({ open, onClose, initialTopic = "", onNavigate }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [topicId, setTopicId] = useState(initialTopic || "");

  useEffect(() => {
    if (open) {
      setTopicId(initialTopic || "");
      setQuery("");
    }
  }, [open, initialTopic]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return HOW_TO_TOPICS;
    const words = q.split(/\s+/);
    return HOW_TO_TOPICS.filter((t) => {
      const hay = `${t.title} ${t.keywords || ""} ${t.steps.join(" ")}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [query]);

  if (!open) return null;
  const topic = HOW_TO_TOPICS.find((t) => t.id === topicId) || null;

  const take = (to) => {
    onClose?.();
    onNavigate?.();
    goTo(navigate, to);
  };

  return createPortal(
    <div className="htg-overlay" onClick={onClose}>
      <div className="htg-card" role="dialog" aria-modal="true" aria-labelledby="htg-title" onClick={(e) => e.stopPropagation()}>
        <div className="htg-head">
          <div>
            <h2 id="htg-title">How do I…?</h2>
            <p className="htg-muted">Where to add everything. "Take me there" opens the exact screen.</p>
          </div>
          <button type="button" className="htg-x" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className={`htg-body ${topic ? "has-topic" : ""}`}>
          <aside className="htg-list">
            <input
              className="htg-search"
              type="search"
              placeholder="Search: pay sub, deposit, link jobs…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            {HOW_TO_GROUPS.map((g) => {
              const items = matches.filter((t) => t.group === g);
              if (!items.length) return null;
              return (
                <div key={g} className="htg-group">
                  <div className="htg-group-name">{g}</div>
                  {items.map((t) => (
                    <button key={t.id} type="button" className={`htg-item ${t.id === topicId ? "is-on" : ""}`} onClick={() => setTopicId(t.id)}>
                      {t.title}
                    </button>
                  ))}
                </div>
              );
            })}
            {!matches.length && <p className="htg-muted">Nothing matches. Try one word, like "check" or "sub".</p>}
          </aside>

          <section className="htg-detail">
            {!topic ? (
              <div className="htg-empty">
                <p>
                  <b>Pick a question on the left.</b>
                </p>
                <p className="htg-muted">Each one shows the steps and a button that takes you right to the screen.</p>
              </div>
            ) : (
              <>
                <button type="button" className="htg-back" onClick={() => setTopicId("")}>
                  ‹ All questions
                </button>
                <div className="htg-group-name">{topic.group}</div>
                <h3>{topic.title}</h3>
                <ol className="htg-steps">
                  {topic.steps.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ol>
                {topic.tip && <p className="htg-tip">{topic.tip}</p>}
                <div className="htg-links">
                  {topic.links.map((l) => (
                    <button key={l.label} type="button" className="htg-go" onClick={() => take(l.to)}>
                      Take me there: {l.label} →
                    </button>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** A "? How do I…" button that opens the guide (optionally on a topic). */
export function HowToButton({ topic = "", label = "? How do I…", onNavigate, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={`htg-open ${className}`} onClick={() => setOpen(true)}>
        {label}
      </button>
      <HowToGuide open={open} onClose={() => setOpen(false)} initialTopic={topic} onNavigate={onNavigate} />
    </>
  );
}
